import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TpiTourData, TpiTourKey } from '@typeRoots/common/tourProgress';
import { RootState } from 'src/store/store';

/**
 * Tour Slice State
 * Stores tour completion data synced from backend
 */
interface TourState {
  tourData: TpiTourData | null;
  isLoading: boolean;
}

const initialState: TourState = {
  tourData: null,
  isLoading: false,
};

/**
 * Tour Slice
 * Manages tour completion state in Redux
 */
const tourSlice = createSlice({
  name: 'tour',
  initialState,
  reducers: {
    /**
     * Sync tour data from backend to Redux store
     */
    setTourProgress(state, action: PayloadAction<TpiTourData>) {
      state.tourData = action.payload;
    },

    /**
     * Optimistically mark a tour as complete
     * Used for immediate UI updates before backend confirms
     */
    optimisticMarkComplete(state, action: PayloadAction<TpiTourKey>) {
      if (state.tourData) {
        state.tourData[action.payload] = true;
      }
    },

    /**
     * Rollback optimistic tour completion
     * Used when backend save fails to keep state in sync
     */
    rollbackTourComplete(state, action: PayloadAction<TpiTourKey>) {
      if (state.tourData) {
        state.tourData[action.payload] = false;
      }
    },

    /**
     * Set loading state
     */
    setTourLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    /**
     * Clear all tour data (useful for logout)
     */
    clearTourData(state) {
      state.tourData = null;
    },
  },
});

export const {
  setTourProgress,
  optimisticMarkComplete,
  rollbackTourComplete,
  setTourLoading,
  clearTourData,
} = tourSlice.actions;

/**
 * Selectors
 */
export const selectTourData = (state: RootState) => state.tour.tourData;
export const selectIsTourCompleted = (state: RootState, tourKey: TpiTourKey): boolean => {
  return state.tour.tourData?.[tourKey] ?? false;
};
export const selectTourLoading = (state: RootState) => state.tour.isLoading;

export default tourSlice.reducer;
